import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import PermissionGate from "../../../../core/layouts/permission-gate";
import {
  allUsersStateAction,
  getAllUsersState,
  UserRole,
} from "../../../../redux/features/user-slice";
import { mgTeam } from "../../../../core/utils/constants";
import { AppDispatch } from "../../../../redux/store";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../../redux/features/common-slice";
import StyledSelect from "../../../../components/styled-select";
import { OptionValue } from "../../../../types/component";
import {
  isSelectMultiValue,
  isSelectSingleValue,
} from "../../../../core/utils";
import Button from "../../../../components/button";
import {
  ExportFormat,
  ExportType,
} from "../../../../redux/features/data-export.slice";
import StyledLabel from "../../../../components/styled-label";
import { getUserState } from "../../../../redux/features/auth-slice";
import SwitchTab from "../../../../components/switch-tab";
import {
  CommonStatus,
  getGroupTeamState,
  groupTeamStateAction,
} from "../../../../redux/features/other-slice";
import Input from "../../../../components/input";
import {
  clearToggleScheduleStatusState,
  clearUpdateScheduleState,
  fetchScheduleByIdAction,
  getFetchScheduleByIdState,
  getToggleScheduleStatusState,
  getUpdateScheduleState,
  ScheduleExportFrequency,
  ScheduleExportPeriod,
  ScheduleExportType,
  ScheduleReportsRequest,
  ToggleScheduleStatusRequest,
  toggleScheduleStatusStateAction,
  updateScheduleStateAction,
} from "../../../../redux/features/schedule-export.slice";
import ToastComponent from "../../../../components/toast";

export default function ScheduleDetailPage() {
  const dispatch = useDispatch<AppDispatch>();
  let { id } = useParams();
  const user = useSelector(getUserState).data;
  const usersState = useSelector(getAllUsersState);
  const groupTeamsState = useSelector(getGroupTeamState);
  const scheduleState = useSelector(getFetchScheduleByIdState).data;
  const updateScheduleState = useSelector(getUpdateScheduleState);
  const errorState = useSelector(getErrorState);
  const toggleScheduleStatusState = useSelector(getToggleScheduleStatusState);
  const [exportByTeams, setExportByTeams] = useState(false);

  const fetchAllUsers = useCallback(async () => {
    if (
      user &&
      [
        UserRole.Admin,
        UserRole.Supervisor,
        UserRole.Manager,
        UserRole.Leader,
      ].includes(user.role)
    ) {
      await dispatch(
        allUsersStateAction({
          teamIds: user.team.id === mgTeam.id ? [] : [user.team.id],
          roles: [UserRole.Admin, UserRole.Supervisor].includes(user.role)
            ? []
            : user.role === UserRole.Manager
            ? [UserRole.Leader, UserRole.Contributor, UserRole.Member]
            : [UserRole.Contributor, UserRole.Member],
        })
      );
      if (user.role === UserRole.Admin && user.team.id === mgTeam.id) {
        await dispatch(
          groupTeamStateAction({
            status: [CommonStatus.Active, CommonStatus.Inactive],
          })
        );
      }
    }
  }, [dispatch, user]);

  const fetchScheduleDetail = useCallback(async () => {
    if (id) {
      await dispatch(fetchScheduleByIdAction(id));
    }
  }, [dispatch, id]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 1000);
    },
    [dispatch]
  );

  const updateExportData = async (values: ScheduleReportsRequest) => {
    let selectedUsers;
    if (exportByTeams) {
      selectedUsers = usersState.data.filter((user) => {
        return (
          user.groupTeam?.id && values.groupTeamIds.includes(user.groupTeam?.id)
        );
      });
    } else {
      selectedUsers = values.userIds
        .map((userId) => usersState.data.find((e) => e.id === userId))
        .filter(Boolean);
    }

    const checkUser =
      user && [UserRole.Contributor, UserRole.Member].includes(user.role);
    const validUsers = checkUser ? [user] : selectedUsers;
    const userIds = validUsers.map((selectedUser) => selectedUser?.id);

    try {
      if (id) {
        await dispatch(
          updateScheduleStateAction({
            id,
            userIds,
            reportName: values.reportName,
            frequency: values.frequency,
            period: values.period,
            reportType: values.reportType,
          })
        );
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const submitToggleScheduleStatus = async (
    values: ToggleScheduleStatusRequest
  ) => {
    await dispatch(toggleScheduleStatusStateAction(values));
  };

  const validateFrequency = (value: string | undefined) => {
    if (value === undefined || value === "") {
      return false;
    }
    return Object.values(ScheduleExportFrequency).includes(
      value as ScheduleExportFrequency
    );
  };

  const validatePeriod = (value: string | undefined) => {
    if (value === undefined || value === "") {
      return false;
    }
    return Object.values(ScheduleExportPeriod).includes(
      value as ScheduleExportPeriod
    );
  };

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: "data", path: null, title: "Data" },
        {
          id: "scheduleReports",
          path: -1,
          title: "Schedule Reports",
        },
        {
          id: "update",
          path: null,
          title: "Update",
        },
      ])
    );
  }, []);

  useEffect(() => {
    fetchAllUsers();
    fetchScheduleDetail();
  }, [fetchAllUsers, fetchScheduleDetail]);

  useEffect(() => {
    if (updateScheduleState.data && updateScheduleState.data.success) {
      handleSuccessToast(
        updateScheduleState.data.message,
        clearUpdateScheduleState
      );
      fetchScheduleDetail();
    }
  }, [updateScheduleState, handleSuccessToast]);

  useEffect(() => {
    if (toggleScheduleStatusState.success) {
      handleSuccessToast("Status is changed", clearToggleScheduleStatusState);
      fetchScheduleDetail();
    }
  }, [toggleScheduleStatusState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate categories={[]} roles={[]}>
      <Helmet title="Schedule Reports" />
      {scheduleState && (
        <Formik
          initialValues={
            {
              reportName: scheduleState.reportName,
              reportType: scheduleState.reportType,
              userIds: scheduleState.users.map((e) => e.id),
              groupTeamIds: [],
              frequency: scheduleState.frequency,
              period: scheduleState.period,
            } as ScheduleReportsRequest
          }
          validationSchema={Yup.object().shape({
            reportType: Yup.string()
              .min(1, "You must select at least one type")
              .required("This field is required"),
            userIds: exportByTeams
              ? Yup.array().notRequired()
              : Yup.array<String>()
                  .required("User(s) is required")
                  .min(1, "You must select at least one User"),
            groupTeamIds: exportByTeams
              ? Yup.array<String>()
                  .required("Team(s) is required")
                  .min(1, "You must select at least one Team")
              : Yup.array().notRequired(),
            reportName: Yup.string().required("This field is required"),
            frequency: Yup.string()
              .required("This field is required")
              .test(
                "is-valid-enum",
                "invalid option selected",
                validateFrequency
              ),
            period: Yup.string()
              .required("This field is required")
              .test("is-valid-enum", "invalid option selected", validatePeriod),
          })}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false);
            updateExportData(values);
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            isSubmitting,
            setValues,
            dirty,
          }) => (
            <Form
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              className="h-full overflow-y-auto"
            >
              <div
                className="h-full grid grid-cols-5 gap-4"
                style={{
                  height: "calc(100% - 40px)",
                }}
              >
                <div className="col-span-4 grid grid-cols-2 gap-4 ">
                  <div className="col-span-1 flex flex-col space-y-4  bg-background-default p-4 rounded-lg shadow h-fit">
                    <label className="text-base text-text-title">
                      Report schedule
                    </label>
                    <div>
                      <Input
                        label="Name *"
                        type="text"
                        id="reportName"
                        value={values.reportName}
                        onChange={handleChange}
                        errorText={errors.reportName}
                      />
                    </div>
                    <div>
                      <StyledLabel label="Type" />
                      <StyledSelect
                        loading={false}
                        options={Object.values(ExportType).map((e) => ({
                          value: e,
                          label: e.capitalizeWords(),
                        }))}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: values.reportType,
                          label: values.reportType,
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              reportType: value.value as ScheduleExportType,
                            });
                          }
                        }}
                      />
                    </div>
                    <div>
                      <StyledLabel label="Frequency" />
                      <StyledSelect
                        loading={false}
                        options={Object.values(ScheduleExportFrequency).map(
                          (e: ScheduleExportFrequency) => ({
                            value: e,
                            label: e.capitalizeWords(),
                          })
                        )}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: values.frequency,
                          label: values.frequency.capitalizeWords(),
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              frequency: value.value as ScheduleExportFrequency,
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-span-1 flex flex-col space-y-4">
                    <div>
                      <StyledLabel label="Format" />
                      <StyledSelect
                        loading={false}
                        options={Object.values(ExportFormat).map((e) => ({
                          value: e,
                          label: e.capitalize(),
                        }))}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: ExportFormat.Excel,
                          label: ExportFormat.Excel.capitalize(),
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          // if (value && isSelectSingleValue(value)) {
                          //   setValues({
                          //     ...values,
                          //     format: value.value as ExportFormat,
                          //   });
                          // }
                        }}
                      />
                    </div>
                    <div>
                      <StyledLabel label="Period" />
                      <StyledSelect
                        loading={false}
                        options={Object.values(ScheduleExportPeriod).map(
                          (e: ScheduleExportPeriod) => ({
                            value: e,
                            label: e.capitalizeWords(),
                          })
                        )}
                        isClearable={false}
                        menuPosition="fixed"
                        value={{
                          value: values.period,
                          label: values.period.capitalizeWords(),
                        }}
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              period: value.value as ScheduleExportPeriod,
                            });
                          }
                        }}
                      />
                    </div>
                    {user &&
                      ![UserRole.Contributor, UserRole.Member].includes(
                        user.role
                      ) && (
                        <div>
                          <div className="flex items-center space-x-4 pb-1">
                            <label className="text-xs text-text-title mb-1">
                              Export by:
                            </label>
                            <SwitchTab
                              options={["Teams", "Users"]}
                              tab={exportByTeams ? "Teams" : "Users"}
                              setTab={(value: string) =>
                                setExportByTeams(value === "Teams")
                              }
                            />
                          </div>
                          {!exportByTeams ? (
                            <StyledSelect
                              isCloseMenuOnSelect={false}
                              loading={usersState.loading}
                              options={usersState.data.map((e) => ({
                                value: e.id,
                                label: `${e.name} - ${e.email}`,
                              }))}
                              isClearable={false}
                              multiple={true}
                              menuPosition="fixed"
                              value={
                                values.userIds
                                  .map((userId) => {
                                    const user = usersState.data.find(
                                      (e) => e.id === userId
                                    );
                                    return user
                                      ? {
                                          value: user.id,
                                          label: `${user.name}`,
                                        }
                                      : undefined;
                                  })
                                  .filter(Boolean) as OptionValue[]
                              }
                              onChange={(
                                value:
                                  | SingleValue<OptionValue>
                                  | MultiValue<OptionValue>
                              ) => {
                                if (value && isSelectMultiValue(value)) {
                                  const selectedUserIds = value.map(
                                    (v) => v.value as string
                                  );
                                  setValues({
                                    ...values,
                                    userIds: selectedUserIds,
                                  });
                                } else {
                                  setValues({
                                    ...values,
                                    userIds: [],
                                  });
                                }
                              }}
                            />
                          ) : (
                            <StyledSelect
                              isCloseMenuOnSelect={false}
                              loading={groupTeamsState.loading}
                              options={groupTeamsState.data.map((e) => ({
                                value: e.id,
                                label: `${e.name}`,
                              }))}
                              isClearable={false}
                              multiple={true}
                              menuPosition="fixed"
                              value={
                                values.groupTeamIds
                                  .map((groupTeamId) => {
                                    const groupTeam = groupTeamsState.data.find(
                                      (e) => e.id === groupTeamId
                                    );
                                    return groupTeam
                                      ? {
                                          value: groupTeam.id,
                                          label: `${groupTeam.name}`,
                                        }
                                      : undefined;
                                  })
                                  .filter(Boolean) as OptionValue[]
                              }
                              onChange={(
                                value:
                                  | SingleValue<OptionValue>
                                  | MultiValue<OptionValue>
                              ) => {
                                if (value && isSelectMultiValue(value)) {
                                  const selectedGroupTeamIds = value.map(
                                    (v) => v.value as string
                                  );
                                  setValues({
                                    ...values,
                                    groupTeamIds: selectedGroupTeamIds,
                                  });
                                } else {
                                  setValues({
                                    ...values,
                                    groupTeamIds: [],
                                  });
                                }
                              }}
                            />
                          )}
                        </div>
                      )}
                  </div>
                </div>
                {/* <div className="col-span-2 bg-background-default shadow rounded-lg"></div> */}
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  label="Update"
                  loading={
                    updateScheduleState.loading ||
                    toggleScheduleStatusState.loading
                  }
                  type="submit"
                  disabled={!dirty}
                />
                <Button
                  label={
                    scheduleState?.isActive === true ? "Deactive" : "Active"
                  }
                  variant="outlined"
                  type="button"
                  loading={
                    updateScheduleState.loading ||
                    toggleScheduleStatusState.loading
                  }
                  onClick={() =>
                    submitToggleScheduleStatus({
                      id: scheduleState?.id ?? "",
                      isActive: scheduleState?.isActive === true ? false : true,
                    })
                  }
                  exClassName={
                    scheduleState?.isActive === true
                      ? "text-error-main border border-error-main"
                      : ""
                  }
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
      <ToastComponent id="scheduleReportDetail" />
    </PermissionGate>
  );
}
